import { View, Text, ActivityIndicator, Platform } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import {
  ONBOARDING_COMPLETE_STEP,
  ONBOARDING_ROUTES,
  ONBOARDING_TOTAL_STEPS,
  getOnboardingStepValueForVisibleIndex,
} from '@perdiem/trpc/onboarding';
import { shuffledFireLoadingMessages } from '../../lib/fire-loading-messages';
import { useNativeFireCalculation } from '../../hooks/useNativeFireCalculation';
import {
  trackFireCalculationCompleted,
  trackFireCalculationFailed,
  trackFireCalculationRequested,
  trackFireCalculationStatusChanged,
  trackOnboardingCompleted,
  trackOnboardingStepCompleted,
} from '../../lib/product-events';
import { OnboardingShell, Button, Card, PlanHealthIssueList, WebHead } from '../../components';
import { trpc } from '../../lib/trpc';

const ONBOARDING_FIRE_EVENT_METADATA = {
  hasExistingPlan: false,
  trigger: 'initial_calculate' as const,
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function getStoredStepForOnboardingRoute(href: string) {
  const index = ONBOARDING_ROUTES.findIndex((route) => route === href);
  if (index < 0) return null;
  return getOnboardingStepValueForVisibleIndex(index);
}

function appendReturnToChecklistParam(href: string) {
  return `${href}${href.includes('?') ? '&' : '?'}returnToChecklist=1`;
}

/**
 * Final onboarding step — FIRE calculation.
 *
 * Web uses the streaming progress subscription. Native kicks off the queued
 * recalculation path and polls recalc status so the request does not get
 * cancelled mid-flight.
 */
export default function Step8Calculating() {
  const router = useRouter();
  const pathname = usePathname();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusLabel, setStatusLabel] = useState('Starting…');
  const [loadingMessages, setLoadingMessages] = useState(() => shuffledFireLoadingMessages());
  const [messageIndex, setMessageIndex] = useState(0);
  const [isLoadingFirePlanForHandoff, setIsLoadingFirePlanForHandoff] = useState(false);
  const [planHealthAccepted, setPlanHealthAccepted] = useState(false);
  const requestStartedAtRef = useRef<number | null>(null);
  const lastReportedStatusRef = useRef<string | null>(null);
  const hasRequestedWebCalculationRef = useRef(false);
  const hasReportedTerminalEventRef = useRef(false);
  const completedFiYearRef = useRef<number | null>(null);
  const completedFiAgeRef = useRef<number | null>(null);

  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();
  const utils = trpc.useUtils();
  const planHealthQ = trpc.planHealth.scan.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const planHealthIssues = planHealthQ.data?.issues ?? [];
  const planHealthPassed = planHealthQ.isSuccess && planHealthIssues.length === 0;
  const calculationUnlocked = planHealthAccepted || planHealthPassed;
  const isCheckingPlanHealth = planHealthQ.isLoading || (planHealthQ.isFetching && !planHealthQ.data);
  const nativeCalculation = useNativeFireCalculation({
    queuedLabel: 'Preparing projections…',
    runningLabel: 'Running simulations…',
    doneLabel: 'Complete!',
    screen: 'onboarding',
    source: 'onboarding',
    metadata: ONBOARDING_FIRE_EVENT_METADATA,
  });
  const {
    error: nativeError,
    isCalculating: isNativeCalculating,
    isDone: isNativeDone,
    progress: nativeProgress,
    reset: resetNativeCalculation,
    start: startNativeCalculation,
    statusLabel: nativeStatusLabel,
  } = nativeCalculation;

  const labels: Record<string, string> = {
    starting: 'Loading your data…',
    precalculating: 'Preparing projections…',
    simulating: 'Running simulations…',
    saving: 'Saving results…',
    done: 'Complete!',
  };

  trpc.fire.calculationProgress.useSubscription(undefined, {
    enabled: Platform.OS === 'web' && calculationUnlocked && !done && !error,
    onData(event) {
      if (event.status !== 'done' && event.status !== 'error' && lastReportedStatusRef.current !== event.status) {
        lastReportedStatusRef.current = event.status;
        trackFireCalculationStatusChanged({
          route: pathname,
          screen: 'onboarding',
          source: 'onboarding',
          transport: 'subscription',
          status: event.status,
          progress: event.progress,
          metadata: ONBOARDING_FIRE_EVENT_METADATA,
        });
      }

      setProgress(event.progress);
      setStatusLabel(labels[event.status] ?? event.status);
      if (event.status === 'done') {
        completedFiYearRef.current = event.fiYear ?? null;
        completedFiAgeRef.current = event.fiAge ?? null;
        setDone(true);
        if (!hasReportedTerminalEventRef.current) {
          hasReportedTerminalEventRef.current = true;
          trackFireCalculationCompleted({
            route: pathname,
            screen: 'onboarding',
            source: 'onboarding',
            transport: 'subscription',
            durationMs:
              requestStartedAtRef.current != null
                ? Date.now() - requestStartedAtRef.current
                : null,
            fiYear: event.fiYear ?? null,
            fiAge: event.fiAge ?? null,
            metadata: ONBOARDING_FIRE_EVENT_METADATA,
          });
        }
      }
      if (event.status === 'error') {
        if (!hasReportedTerminalEventRef.current) {
          hasReportedTerminalEventRef.current = true;
          trackFireCalculationFailed({
            route: pathname,
            screen: 'onboarding',
            source: 'onboarding',
            transport: 'subscription',
            durationMs:
              requestStartedAtRef.current != null
                ? Date.now() - requestStartedAtRef.current
                : null,
            errorMessage: event.error ?? 'Unknown error',
            metadata: ONBOARDING_FIRE_EVENT_METADATA,
          });
        }
        setError(humaniseCalcError(event.error ?? 'Unknown error'));
      }
    },
    onError(err) {
      if (!hasReportedTerminalEventRef.current) {
        hasReportedTerminalEventRef.current = true;
        trackFireCalculationFailed({
          route: pathname,
          screen: 'onboarding',
          source: 'onboarding',
          transport: 'subscription',
          durationMs:
            requestStartedAtRef.current != null
              ? Date.now() - requestStartedAtRef.current
              : null,
          errorMessage: err.message,
          metadata: ONBOARDING_FIRE_EVENT_METADATA,
        });
      }
      setError(humaniseCalcError(err.message));
    },
  });

  useEffect(() => {
    if (
      Platform.OS !== 'web'
      || !calculationUnlocked
      || done
      || error
      || hasRequestedWebCalculationRef.current
    ) {
      return;
    }

    hasRequestedWebCalculationRef.current = true;
    hasReportedTerminalEventRef.current = false;
    lastReportedStatusRef.current = null;
    requestStartedAtRef.current = Date.now();
    trackFireCalculationRequested({
      route: pathname,
      screen: 'onboarding',
      source: 'onboarding',
      transport: 'subscription',
      metadata: ONBOARDING_FIRE_EVENT_METADATA,
    });
  }, [calculationUnlocked, done, error, pathname]);

  useEffect(() => {
    if (
      Platform.OS === 'web'
      || !calculationUnlocked
      || done
      || error
      || isNativeCalculating
      || isNativeDone
    ) return;
    void startNativeCalculation();
  }, [calculationUnlocked, done, error, isNativeCalculating, isNativeDone, startNativeCalculation]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    setProgress(nativeProgress);
    setStatusLabel(nativeStatusLabel);
    if (isNativeDone) setDone(true);
    if (nativeError) setError(humaniseCalcError(nativeError));
  }, [isNativeDone, nativeError, nativeProgress, nativeStatusLabel]);

  useEffect(() => {
    if (!calculationUnlocked || done || error) return;

    const intervalId = setInterval(() => {
      setMessageIndex((currentIndex) => (currentIndex + 1) % loadingMessages.length);
    }, 4800);

    return () => clearInterval(intervalId);
  }, [calculationUnlocked, done, error, loadingMessages]);

  const handlePlanHealthIssuePress = async (_issue: unknown, href: string) => {
    const storedStep = getStoredStepForOnboardingRoute(href);
    if (storedStep != null) {
      await advanceMutation.mutateAsync({ step: storedStep });
      await utils.households.get.invalidate();
    }
    router.push((storedStep != null ? appendReturnToChecklistParam(href) : href) as never);
  };

  const retry = () => {
    resetNativeCalculation();
    requestStartedAtRef.current = null;
    lastReportedStatusRef.current = null;
    hasRequestedWebCalculationRef.current = false;
    hasReportedTerminalEventRef.current = false;
    completedFiYearRef.current = null;
    completedFiAgeRef.current = null;
    setError(null);
    setDone(false);
    setProgress(0);
    setStatusLabel('Starting…');
    setLoadingMessages(shuffledFireLoadingMessages());
    setMessageIndex(0);
    setIsLoadingFirePlanForHandoff(false);
  };

  const handleFinish = async () => {
    if (isLoadingFirePlanForHandoff || advanceMutation.isPending) return;

    setError(null);
    setIsLoadingFirePlanForHandoff(true);
    let plan;
    try {
      plan = await fetchFreshFirePlanForHandoff();
    } catch {
      plan = null;
    }
    if (!plan) {
      setIsLoadingFirePlanForHandoff(false);
      setError('Your FIRE plan finished calculating, but the app could not load it yet. Please try again.');
      return;
    }

    try {
      await advanceMutation.mutateAsync({ step: ONBOARDING_COMPLETE_STEP });
      await Promise.all([
        utils.bootstrap.get.invalidate(),
        utils.households.get.invalidate(),
        utils.fire.getLastRecalcImpact.invalidate(),
        utils.fire.listRecalcHistory.invalidate(),
      ]);
      trackOnboardingStepCompleted({
        route: pathname,
        stepNumber: ONBOARDING_TOTAL_STEPS,
        completionKind: 'continue',
        metadata: {
          fiYear: completedFiYearRef.current,
          fiAge: completedFiAgeRef.current,
        },
      });
      trackOnboardingCompleted({
        route: pathname,
        metadata: {
          fiYear: completedFiYearRef.current,
          fiAge: completedFiAgeRef.current,
        },
      });
      router.replace('/(app)/fire-plan');
    } finally {
      setIsLoadingFirePlanForHandoff(false);
    }
  };

  const fetchFreshFirePlanForHandoff = async () => {
    await utils.fire.getPlan.invalidate();

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const plan = await utils.fire.getPlan.fetch();
      if (plan) return plan;
      await sleep(350);
    }

    return null;
  };

  const planHealthFooter =
    !calculationUnlocked && planHealthQ.error ? (
      <View className="gap-3">
        <Button
          variant="secondary"
          onPress={() => {
            void planHealthQ.refetch();
          }}
          loading={planHealthQ.isFetching}
        >
          Try checkup again
        </Button>
        <Button onPress={() => setPlanHealthAccepted(true)}>
          Calculate with current inputs →
        </Button>
      </View>
    ) : !calculationUnlocked && planHealthIssues.length > 0 ? (
      <Button onPress={() => setPlanHealthAccepted(true)}>
        Calculate with current inputs →
      </Button>
    ) : null;

  return (
    <>
      <WebHead><title>Step 8: Calculating – Per Diem</title></WebHead>
      <OnboardingShell
        step={7}
      title={
        !calculationUnlocked
          ? 'Plan Health Checkup'
          : done
          ? 'FIRE Plan Ready! 🎉'
          : 'Calculating Your FIRE Plan'
      }
      subtitle={
        !calculationUnlocked
          ? 'Before the first calculation, Per Diem checks for missing inputs that could skew your FI age.'
          : done
          ? 'Your Monte Carlo simulation is complete. Welcome to Per Diem!'
          : 'Running 1,000 Monte Carlo simulations across your lifetime. This takes a few seconds.'
      }
      footer={
        planHealthFooter ?? (done ? (
          <Button
            testID="onboarding-calculation-finish-button"
            accessibilityLabel="See my FIRE plan"
            onPress={handleFinish}
            loading={advanceMutation.isPending || isLoadingFirePlanForHandoff}
          >
            See My FIRE Plan →
          </Button>
        ) : error ? (
          <Button variant="secondary" onPress={retry}>
            Retry
          </Button>
        ) : null)
      }
    >
      {!calculationUnlocked ? (
        <View className="gap-4">
          {isCheckingPlanHealth && (
            <Card className="items-center py-8">
              <ActivityIndicator size="large" color="#2B7A50" />
              <Text className="text-base font-semibold text-gray-800 dark:text-gray-100 mt-4 text-center">
                Running plan health checkup…
              </Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                If everything looks ready, your FIRE calculation will start automatically.
              </Text>
            </Card>
          )}

          {planHealthQ.error && (
            <Card className="border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30">
              <Text className="text-base font-semibold text-amber-900 dark:text-amber-200">
                Checkup could not finish
              </Text>
              <Text className="text-sm text-amber-800 dark:text-amber-300 mt-2">
                You can retry the checkup, or calculate now with your current inputs.
              </Text>
            </Card>
          )}

          {!isCheckingPlanHealth && !planHealthQ.error && planHealthIssues.length > 0 && (
            <>
              <Card className="border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30">
                <Text className="text-base font-semibold text-amber-900 dark:text-amber-200">
                  {planHealthIssues.length} suggested {planHealthIssues.length === 1 ? 'fix' : 'fixes'} before calculating
                </Text>
                <Text className="text-sm text-amber-800 dark:text-amber-300 mt-2">
                  These items may affect your FI age. You can fix them now or calculate with the information you already entered.
                </Text>
              </Card>
              <PlanHealthIssueList
                issues={planHealthIssues}
                isFetching={planHealthQ.isFetching}
                navigationContext="onboarding"
                onIssuePress={handlePlanHealthIssuePress}
                onRefetch={() => {
                  void planHealthQ.refetch();
                }}
                scrollable={false}
              />
            </>
          )}
        </View>
      ) : (
        <View className="items-center py-8">
          {!done && !error && (
          <>
            <ActivityIndicator size="large" color="#2B7A50" />
            <Text className="text-sm text-gray-600 mt-4 text-center">
              {statusLabel} {Math.round(progress * 100)}%
            </Text>
            <Text className="text-sm text-gray-500 mt-3 text-center">
              {loadingMessages[messageIndex]}
            </Text>
            <View className="w-full mt-6 bg-gray-200 rounded-full h-2">
              <View
                className="bg-forest-500 h-2 rounded-full"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </View>
          </>
        )}

        {done && (
          <View className="items-center">
            <Text className="text-6xl mb-4">🎯</Text>
            <Text className="text-xl font-bold text-gray-800 text-center">
              Your plan is ready!
            </Text>
            <Text className="text-sm text-gray-500 text-center mt-2">
              Tap below to see your year-by-year projections, Monte Carlo fan chart, and account drawdown schedule.
            </Text>
          </View>
        )}

        {error && (
          <View className="w-full rounded-xl bg-red-50 px-5 py-4">
            <Text className="text-base font-semibold text-red-700 mb-1">Calculation failed</Text>
            <Text className="text-sm text-red-600">{error}</Text>
          </View>
        )}
      </View>
      )}
      </OnboardingShell>
    </>
  );
}

/**
 * Turn a raw tRPC / server error message into something actionable for the user.
 * Add more cases here as new error conditions are discovered.
 */
function humaniseCalcError(msg: string): string {
  const m = msg.toLowerCase();

  if (m.includes('no fire plan')) {
    return 'Could not create your FIRE plan record. Please go back and try again.';
  }
  if (m.includes('no person') || m.includes('primary person')) {
    return 'Your profile is incomplete. Please go back to the profile step and save your details.';
  }
  if (m.includes('birth year') || m.includes('birthyear')) {
    return 'Your birth year is missing. Go back to the profile step and enter your birth year.';
  }
  if (m.includes('unauthorized') || m.includes('unauthenticated')) {
    return 'Your session has expired. Please sign out and sign back in, then try again.';
  }
  if (m.includes('worker') || m.includes('exit code')) {
    return 'The simulation engine crashed unexpectedly. Please retry — if the problem persists, contact support.';
  }
  if (m.includes('timeout')) {
    return 'The calculation timed out. Please retry — it usually completes in 2–5 seconds.';
  }

  // Fall back to the raw message so we can diagnose unknown errors
  return msg || 'An unexpected error occurred. Please retry.';
}
